import { Link, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import {
  House, MapTrifold, MapPin, SquaresFour, Car, IdentificationCard, Receipt,
  UsersThree, Wallet as WalletIcon, ShieldWarning, ListChecks, Gauge, CurrencyDollar,
  Prohibit, SignOut, ClipboardText,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Executive", icon: House, key: "executive", roles: "*" },
  { to: "/operations", label: "Operations", icon: Gauge, key: "operations",
    roles: ["super_admin","property_admin","parking_manager","security_guard","receptionist"] },
  { to: "/finance", label: "Finance", icon: CurrencyDollar, key: "finance",
    roles: ["super_admin","property_admin","finance_officer","parking_manager"] },
  { to: "/map", label: "Parking Map", icon: MapTrifold, key: "map", roles: "*" },
  { to: "/live-map", label: "Live Map", icon: MapPin, key: "live-map", roles: "*" },
  { to: "/slots", label: "Slots", icon: SquaresFour, key: "slots",
    roles: ["super_admin","property_admin","parking_manager"] },
  { to: "/sessions", label: "Entry / Exit", icon: Car, key: "sessions",
    roles: ["super_admin","property_admin","parking_manager","security_guard","receptionist"] },
  { to: "/visitors", label: "Visitors", icon: IdentificationCard, key: "visitors", roles: "*" },
  { to: "/vehicles", label: "Vehicles", icon: Car, key: "vehicles", roles: "*" },
  { to: "/residents", label: "Residents", icon: UsersThree, key: "residents",
    roles: ["super_admin","property_admin","parking_manager","receptionist"] },
  { to: "/wallet", label: "Wallets", icon: WalletIcon, key: "wallet", roles: "*" },
  { to: "/billing", label: "Billing Config", icon: Receipt, key: "billing",
    roles: ["super_admin","property_admin","finance_officer"] },
  { to: "/violations", label: "Violations", icon: ShieldWarning, key: "violations",
    roles: ["super_admin","property_admin","parking_manager","security_guard"] },
  { to: "/blacklist", label: "Blacklist", icon: Prohibit, key: "blacklist",
    roles: ["super_admin","property_admin","parking_manager","security_guard"] },
  { to: "/audit", label: "Audit Log", icon: ClipboardText, key: "audit",
    roles: ["super_admin","property_admin","parking_manager","finance_officer","auditor"] },
];

function canSee(item, role) {
  if (item.roles === "*") return true;
  if (role === "super_admin") return true;
  return item.roles.includes(role);
}

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  return (
    <aside className="h-full w-64 shrink-0 bg-[#FFFDF7] border-r border-[#E2D8BE] flex flex-col">
      <div className="px-5 py-5 border-b border-[#E2D8BE] flex items-center gap-3 bg-white">
        <div className="h-14 w-20 rounded-xl bg-white border border-[#E2D8BE] shadow-sm flex items-center justify-center p-2">
          <img
            src="/amaia-logo.png"
            alt="Amaia Steps"
            className="max-h-10 max-w-full object-contain"
          />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#B8923A]">AMAIA STEPS</p>
          <p className="font-heading text-sm font-bold leading-tight text-[#1F2933]">Parking System</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV.filter((n) => canSee(n, user?.role)).map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={TID.navItem(n.key)}
              onClick={onNavigate}
              className={`flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-md text-sm transition-colors ${
                active ? "bg-[#EAF4E6] text-[#2E7D32]" : "text-[#374151] hover:bg-[#F1EFE6] hover:text-[#1F2933]"
              }`}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              <span>{n.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-[#E7E2D4]">
        <div className="px-3 py-2 mb-2 rounded-md bg-white border border-[#E2D8BE]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#8A7B5C]">
            {ROLE_LABELS[user?.role] || user?.role}
          </p>
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-[#8A7B5C] truncate">{user?.email}</p>
        </div>
        <button
          data-testid={TID.logoutBtn}
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-zinc-800 text-sm text-[#374151] hover:bg-[#F1EFE6] hover:text-[#1F2933] transition-colors"
        >
          <SignOut size={16} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
